import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginAPI } from "../api/api";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .login-root {
    min-height: 100vh;
    display: flex;
    font-family: 'DM Sans', sans-serif;
    overflow: hidden;
    position: relative;
    background: #060d1f;
  }

  /* ── animated mesh background ── */
  .login-bg {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
  }
  .login-bg::before {
    content: '';
    position: absolute;
    width: 900px; height: 900px;
    top: -300px; left: -200px;
    background: radial-gradient(circle, rgba(30,91,168,0.45) 0%, transparent 65%);
    animation: bgFloat1 12s ease-in-out infinite alternate;
  }
  .login-bg::after {
    content: '';
    position: absolute;
    width: 700px; height: 700px;
    bottom: -200px; right: -100px;
    background: radial-gradient(circle, rgba(56,189,248,0.2) 0%, transparent 65%);
    animation: bgFloat2 15s ease-in-out infinite alternate;
  }
  @keyframes bgFloat1 {
    from { transform: translate(0,0) scale(1); }
    to   { transform: translate(80px, 60px) scale(1.15); }
  }
  @keyframes bgFloat2 {
    from { transform: translate(0,0) scale(1); }
    to   { transform: translate(-60px, -40px) scale(1.1); }
  }

  /* grid overlay */
  .login-grid {
    position: fixed;
    inset: 0;
    z-index: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent);
  }

  /* floating orbs */
  .orb {
    position: fixed;
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
  }
  .orb-1 {
    width: 120px; height: 120px;
    top: 15%; left: 10%;
    background: rgba(37,99,235,0.12);
    border: 1px solid rgba(37,99,235,0.2);
    animation: orbFloat 8s ease-in-out infinite;
  }
  .orb-2 {
    width: 60px; height: 60px;
    top: 65%; left: 6%;
    background: rgba(56,189,248,0.1);
    border: 1px solid rgba(56,189,248,0.15);
    animation: orbFloat 11s ease-in-out infinite reverse;
  }
  .orb-3 {
    width: 90px; height: 90px;
    top: 30%; right: 8%;
    background: rgba(15,58,125,0.15);
    border: 1px solid rgba(255,255,255,0.06);
    animation: orbFloat 9s ease-in-out infinite 2s;
  }
  .orb-4 {
    width: 40px; height: 40px;
    bottom: 20%; right: 12%;
    background: rgba(56,189,248,0.08);
    border: 1px solid rgba(56,189,248,0.12);
    animation: orbFloat 7s ease-in-out infinite 1s;
  }
  @keyframes orbFloat {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    33%       { transform: translateY(-20px) rotate(5deg); }
    66%       { transform: translateY(10px) rotate(-3deg); }
  }

  /* ── left brand panel ── */
  .login-brand {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 60px 64px;
    position: relative;
    z-index: 2;
    animation: panelSlideIn 1s cubic-bezier(0.16,1,0.3,1) both;
  }
  @keyframes panelSlideIn {
    from { opacity: 0; transform: translateX(-40px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .brand-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(37,99,235,0.15);
    border: 1px solid rgba(37,99,235,0.3);
    border-radius: 20px;
    padding: 5px 14px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: #60a5fa;
    margin-bottom: 28px;
    width: fit-content;
    animation: fadeInUp 0.8s ease-out 0.3s both;
  }
  .brand-eyebrow-dot {
    width: 6px; height: 6px;
    background: #60a5fa;
    border-radius: 50%;
    animation: dotBlink 2s ease-in-out infinite;
  }
  @keyframes dotBlink {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(96,165,250,0.5); }
    50%       { opacity: 0.5; box-shadow: 0 0 8px 4px rgba(96,165,250,0); }
  }

  .brand-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(3rem, 5vw, 4.5rem);
    font-weight: 800;
    line-height: 1.05;
    color: white;
    margin-bottom: 20px;
    animation: fadeInUp 0.8s ease-out 0.45s both;
  }
  .brand-title-accent {
    display: block;
    background: linear-gradient(135deg, #60a5fa 0%, #38bdf8 50%, #818cf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .brand-desc {
    font-size: 15px;
    color: rgba(255,255,255,0.45);
    line-height: 1.7;
    max-width: 380px;
    margin-bottom: 48px;
    animation: fadeInUp 0.8s ease-out 0.6s both;
  }

  .brand-stats {
    display: flex;
    gap: 36px;
    animation: fadeInUp 0.8s ease-out 0.75s both;
  }
  .brand-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .brand-stat-value {
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 700;
    color: white;
    line-height: 1;
  }
  .brand-stat-label {
    font-size: 11px;
    color: rgba(255,255,255,0.35);
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .brand-divider {
    width: 1px;
    height: 36px;
    background: rgba(255,255,255,0.1);
    align-self: center;
  }

  /* ── right form panel ── */
  .login-panel {
    width: 480px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
    position: relative;
    z-index: 2;
  }

  .login-card {
    width: 100%;
    background: rgba(255,255,255,0.04);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 24px;
    padding: 44px 40px;
    position: relative;
    overflow: hidden;
    animation: cardReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 40px 80px rgba(0,0,0,0.5),
      0 0 120px rgba(37,99,235,0.08);
  }
  @keyframes cardReveal {
    from { opacity: 0; transform: translateY(32px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* card top shimmer line */
  .login-card::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 2px;
    background: linear-gradient(90deg, transparent, rgba(96,165,250,0.8), rgba(56,189,248,0.8), transparent);
    animation: shimmerLine 3s ease-in-out 1.2s infinite;
  }
  @keyframes shimmerLine {
    0%   { left: -100%; }
    40%, 100% { left: 100%; }
  }

  /* card corner accent */
  .login-card::after {
    content: '';
    position: absolute;
    top: -1px; right: -1px;
    width: 80px; height: 80px;
    background: radial-gradient(circle at top right, rgba(96,165,250,0.12), transparent 65%);
    border-radius: 0 24px 0 0;
  }

  /* card header */
  .card-logo {
    width: 64px; height: 64px;
    border-radius: 16px;
    background: linear-gradient(135deg, #0a2558, #1e5ba8);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 8px 24px rgba(37,99,235,0.35);
    animation: logoAppear 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.7s both;
    position: relative;
    overflow: hidden;
    padding: 6px;
  }
  .card-logo::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.15), transparent);
    border-radius: 16px;
    pointer-events: none;
  }
  .card-logo img {
    width: 100%; height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 1;
  }
  .card-logo-text {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    font-weight: 800;
    color: white;
    position: relative;
    z-index: 1;
  }

  .card-title {
    font-family: 'Playfair Display', serif;
    font-size: 26px;
    font-weight: 700;
    color: white;
    margin-bottom: 6px;
    animation: fadeInUp 0.7s ease-out 0.8s both;
  }

  .card-subtitle {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 36px;
    animation: fadeInUp 0.7s ease-out 0.9s both;
  }

  /* ── form fields ── */
  .field-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 28px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: fadeInUp 0.6s ease-out both;
  }
  .field:nth-child(1) { animation-delay: 1s; }
  .field:nth-child(2) { animation-delay: 1.1s; }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255,255,255,0.5);
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .field-wrap {
    position: relative;
  }

  .field-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25);
    font-size: 15px;
    pointer-events: none;
    transition: color 0.3s ease;
  }

  .field-input {
    width: 100%;
    padding: 13px 14px 13px 42px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: white;
    outline: none;
    transition: all 0.3s ease;
    caret-color: #60a5fa;
  }
  .field-input::placeholder { color: rgba(255,255,255,0.2); }
  .field-input:focus {
    background: rgba(255,255,255,0.09);
    border-color: rgba(96,165,250,0.6);
    box-shadow: 0 0 0 3px rgba(96,165,250,0.1), 0 0 20px rgba(96,165,250,0.08);
  }
  .field-input:focus + .field-focus-bar { width: 100%; }
  .field-input:disabled { opacity: 0.5; cursor: not-allowed; }

  /* animated bottom bar on focus */
  .field-focus-bar {
    position: absolute;
    bottom: 0; left: 50%;
    transform: translateX(-50%);
    height: 2px;
    width: 0;
    background: linear-gradient(90deg, #2563eb, #38bdf8);
    border-radius: 0 0 10px 10px;
    transition: width 0.4s cubic-bezier(0.16,1,0.3,1);
    pointer-events: none;
  }

  .show-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: rgba(255,255,255,0.3);
    cursor: pointer;
    padding: 4px 6px;
    font-size: 11px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: color 0.2s ease;
  }
  .show-toggle:hover { color: #60a5fa; }

  /* ── error box ── */
  .error-box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.3);
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 20px;
    animation: errorShake 0.4s cubic-bezier(0.36,0.07,0.19,0.97);
  }
  @keyframes errorShake {
    10%, 90%  { transform: translateX(-2px); }
    20%, 80%  { transform: translateX(4px); }
    30%, 50%, 70% { transform: translateX(-4px); }
    40%, 60%  { transform: translateX(4px); }
  }
  .error-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .error-text { font-size: 13px; color: #fca5a5; line-height: 1.5; }

  /* ── submit button ── */
  .submit-btn {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #1d4ed8, #2563eb, #0ea5e9);
    background-size: 200% auto;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    letter-spacing: 0.3px;
    transition: all 0.4s ease;
    position: relative;
    overflow: hidden;
    animation: fadeInUp 0.7s ease-out 1.2s both;
    box-shadow: 0 4px 20px rgba(37,99,235,0.3);
  }
  .submit-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  .submit-btn:hover:not(:disabled)::before { transform: translateX(100%); }
  .submit-btn:hover:not(:disabled) {
    background-position: right center;
    box-shadow: 0 8px 32px rgba(37,99,235,0.45);
    transform: translateY(-1px);
  }
  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .submit-btn:disabled {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.3);
    cursor: not-allowed;
    box-shadow: none;
  }

  .btn-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    z-index: 1;
  }

  .spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.2);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ── footer ── */
  .card-footer {
    text-align: center;
    margin-top: 28px;
    font-size: 12px;
    color: rgba(255,255,255,0.18);
    animation: fadeInUp 0.7s ease-out 1.4s both;
    letter-spacing: 0.2px;
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* responsive */
  @media (max-width: 860px) {
    .login-brand { display: none; }
    .login-panel { width: 100%; }
  }
`;

const MANAGER_USERNAMES = [
  "rajini",
  "rajesh",
  "balaji",
  "magesh",
  "rajasekar",
  "saravanan",
  "mageshkumar",
  "esakiraj",
];

export default function Login() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const login = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await loginAPI(username, password);
      if (response && response.data?.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("username", username);

        const isManager = MANAGER_USERNAMES.includes(
          username.trim().toLowerCase(),
        );

        navigate(isManager ? "/sales-overview" : "/dashboard");
      } else {
        setError(response?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="login-root">
        <div className="login-bg" />
        <div className="login-grid" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="orb orb-4" />

        <div className="login-brand">
          <div className="brand-eyebrow">
            <span className="brand-eyebrow-dot" />
            Enterprise Portal
          </div>
          <h1 className="brand-title">
            Chairman's
            <span className="brand-title-accent">Desk</span>
          </h1>
          <p className="brand-desc">
            Real-time network inventory and operations overview. Monitor
            distributors, LCOs, and customer activity from a single command
            center.
          </p>
          <div className="brand-stats">
            <div className="brand-stat">
              <span className="brand-stat-value">24/7</span>
              <span className="brand-stat-label">Live Data</span>
            </div>
            <div className="brand-divider" />
            <div className="brand-stat">
              <span className="brand-stat-value">100%</span>
              <span className="brand-stat-label">Uptime</span>
            </div>
            <div className="brand-divider" />
            <div className="brand-stat">
              <span className="brand-stat-value">256-bit</span>
              <span className="brand-stat-label">Encryption</span>
            </div>
          </div>
        </div>

        <div className="login-panel">
          <div className="login-card">
            <div className="card-logo">
              <img
                src="../dm_profile.png"
                alt="Chairman Desk"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
              <span className="card-logo-text" style={{ display: "none" }}>
                C
              </span>
            </div>

            <h2 className="card-title">Welcome</h2>
            <p className="card-subtitle">Sign in to access your dashboard</p>

            {error && (
              <div className="error-box" key={error}>
                <span className="error-icon">⚠️</span>
                <p className="error-text">{error}</p>
              </div>
            )}

            <form onSubmit={login}>
              <div className="field-group">
                <div className="field">
                  <label className="field-label">Username</label>
                  <div className="field-wrap">
                    <span
                      className="field-icon"
                      style={{
                        color:
                          focusedField === "username" ? "#60a5fa" : undefined,
                      }}
                    >
                      👤
                    </span>
                    <input
                      className="field-input"
                      type="text"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                      onFocus={() => setFocusedField("username")}
                      onBlur={() => setFocusedField(null)}
                      autoComplete="username"
                    />
                    <div className="field-focus-bar" />
                  </div>
                </div>

                <div className="field">
                  <label className="field-label">Password</label>
                  <div className="field-wrap">
                    <span
                      className="field-icon"
                      style={{
                        color:
                          focusedField === "password" ? "#60a5fa" : undefined,
                      }}
                    >
                      🔒
                    </span>
                    <input
                      className="field-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField(null)}
                      autoComplete="current-password"
                      style={{ paddingRight: "60px" }}
                    />
                    <div className="field-focus-bar" />
                    <button
                      type="button"
                      className="show-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      tabIndex={-1}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </div>

              <button className="submit-btn" type="submit" disabled={loading}>
                <span className="btn-inner">
                  {loading && <span className="spinner" />}
                  {loading ? "Signing in…" : "Sign In →"}
                </span>
              </button>
            </form>

            <p className="card-footer">
              © 2026 Chairman Desk · All rights reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// import { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { loginAPI } from "../api/api";

// /* ─── Inline styles & keyframes injected once ─────────────────────── */
// const STYLES = `
//   @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

//   *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

//   .login-root {
//     min-height: 100vh;
//     display: flex;
//     font-family: 'DM Sans', sans-serif;
//     overflow: hidden;
//     position: relative;
//     background: #060d1f;
//   }

//   /* ── animated mesh background ── */
//   .login-bg {
//     position: fixed;
//     inset: 0;
//     z-index: 0;
//     overflow: hidden;
//   }
//   .login-bg::before {
//     content: '';
//     position: absolute;
//     width: 900px; height: 900px;
//     top: -300px; left: -200px;
//     background: radial-gradient(circle, rgba(30,91,168,0.45) 0%, transparent 65%);
//     animation: bgFloat1 12s ease-in-out infinite alternate;
//   }
//   .login-bg::after {
//     content: '';
//     position: absolute;
//     width: 700px; height: 700px;
//     bottom: -200px; right: -100px;
//     background: radial-gradient(circle, rgba(56,189,248,0.2) 0%, transparent 65%);
//     animation: bgFloat2 15s ease-in-out infinite alternate;
//   }
//   @keyframes bgFloat1 {
//     from { transform: translate(0,0) scale(1); }
//     to   { transform: translate(80px, 60px) scale(1.15); }
//   }
//   @keyframes bgFloat2 {
//     from { transform: translate(0,0) scale(1); }
//     to   { transform: translate(-60px, -40px) scale(1.1); }
//   }

//   /* grid overlay */
//   .login-grid {
//     position: fixed;
//     inset: 0;
//     z-index: 0;
//     background-image:
//       linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
//       linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
//     background-size: 48px 48px;
//     mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent);
//   }

//   /* floating orbs */
//   .orb {
//     position: fixed;
//     border-radius: 50%;
//     z-index: 0;
//     pointer-events: none;
//   }
//   .orb-1 {
//     width: 120px; height: 120px;
//     top: 15%; left: 10%;
//     background: rgba(37,99,235,0.12);
//     border: 1px solid rgba(37,99,235,0.2);
//     animation: orbFloat 8s ease-in-out infinite;
//   }
//   .orb-2 {
//     width: 60px; height: 60px;
//     top: 65%; left: 6%;
//     background: rgba(56,189,248,0.1);
//     border: 1px solid rgba(56,189,248,0.15);
//     animation: orbFloat 11s ease-in-out infinite reverse;
//   }
//   .orb-3 {
//     width: 90px; height: 90px;
//     top: 30%; right: 8%;
//     background: rgba(15,58,125,0.15);
//     border: 1px solid rgba(255,255,255,0.06);
//     animation: orbFloat 9s ease-in-out infinite 2s;
//   }
//   .orb-4 {
//     width: 40px; height: 40px;
//     bottom: 20%; right: 12%;
//     background: rgba(56,189,248,0.08);
//     border: 1px solid rgba(56,189,248,0.12);
//     animation: orbFloat 7s ease-in-out infinite 1s;
//   }
//   @keyframes orbFloat {
//     0%, 100% { transform: translateY(0) rotate(0deg); }
//     33%       { transform: translateY(-20px) rotate(5deg); }
//     66%       { transform: translateY(10px) rotate(-3deg); }
//   }

//   /* ── left brand panel ── */
//   .login-brand {
//     flex: 1;
//     display: flex;
//     flex-direction: column;
//     justify-content: center;
//     padding: 60px 64px;
//     position: relative;
//     z-index: 2;
//     animation: panelSlideIn 1s cubic-bezier(0.16,1,0.3,1) both;
//   }
//   @keyframes panelSlideIn {
//     from { opacity: 0; transform: translateX(-40px); }
//     to   { opacity: 1; transform: translateX(0); }
//   }

//   .brand-eyebrow {
//     display: inline-flex;
//     align-items: center;
//     gap: 8px;
//     background: rgba(37,99,235,0.15);
//     border: 1px solid rgba(37,99,235,0.3);
//     border-radius: 20px;
//     padding: 5px 14px;
//     font-size: 11px;
//     font-weight: 600;
//     letter-spacing: 2.5px;
//     text-transform: uppercase;
//     color: #60a5fa;
//     margin-bottom: 28px;
//     width: fit-content;
//     animation: fadeInUp 0.8s ease-out 0.3s both;
//   }
//   .brand-eyebrow-dot {
//     width: 6px; height: 6px;
//     background: #60a5fa;
//     border-radius: 50%;
//     animation: dotBlink 2s ease-in-out infinite;
//   }
//   @keyframes dotBlink {
//     0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(96,165,250,0.5); }
//     50%       { opacity: 0.5; box-shadow: 0 0 8px 4px rgba(96,165,250,0); }
//   }

//   .brand-title {
//     font-family: 'Playfair Display', serif;
//     font-size: clamp(3rem, 5vw, 4.5rem);
//     font-weight: 800;
//     line-height: 1.05;
//     color: white;
//     margin-bottom: 20px;
//     animation: fadeInUp 0.8s ease-out 0.45s both;
//   }
//   .brand-title-accent {
//     display: block;
//     background: linear-gradient(135deg, #60a5fa 0%, #38bdf8 50%, #818cf8 100%);
//     -webkit-background-clip: text;
//     -webkit-text-fill-color: transparent;
//     background-clip: text;
//   }

//   .brand-desc {
//     font-size: 15px;
//     color: rgba(255,255,255,0.45);
//     line-height: 1.7;
//     max-width: 380px;
//     margin-bottom: 48px;
//     animation: fadeInUp 0.8s ease-out 0.6s both;
//   }

//   .brand-stats {
//     display: flex;
//     gap: 36px;
//     animation: fadeInUp 0.8s ease-out 0.75s both;
//   }
//   .brand-stat {
//     display: flex;
//     flex-direction: column;
//     gap: 4px;
//   }
//   .brand-stat-value {
//     font-family: 'Playfair Display', serif;
//     font-size: 28px;
//     font-weight: 700;
//     color: white;
//     line-height: 1;
//   }
//   .brand-stat-label {
//     font-size: 11px;
//     color: rgba(255,255,255,0.35);
//     font-weight: 500;
//     letter-spacing: 0.5px;
//     text-transform: uppercase;
//   }

//   .brand-divider {
//     width: 1px;
//     height: 36px;
//     background: rgba(255,255,255,0.1);
//     align-self: center;
//   }

//   /* ── right form panel ── */
//   .login-panel {
//     width: 480px;
//     flex-shrink: 0;
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     padding: 40px;
//     position: relative;
//     z-index: 2;
//   }

//   .login-card {
//     width: 100%;
//     background: rgba(255,255,255,0.04);
//     backdrop-filter: blur(24px);
//     -webkit-backdrop-filter: blur(24px);
//     border: 1px solid rgba(255,255,255,0.1);
//     border-radius: 24px;
//     padding: 44px 40px;
//     position: relative;
//     overflow: hidden;
//     animation: cardReveal 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both;
//     box-shadow:
//       0 0 0 1px rgba(255,255,255,0.04) inset,
//       0 40px 80px rgba(0,0,0,0.5),
//       0 0 120px rgba(37,99,235,0.08);
//   }
//   @keyframes cardReveal {
//     from { opacity: 0; transform: translateY(32px) scale(0.96); }
//     to   { opacity: 1; transform: translateY(0) scale(1); }
//   }

//   /* card top shimmer line */
//   .login-card::before {
//     content: '';
//     position: absolute;
//     top: 0; left: -100%;
//     width: 100%; height: 2px;
//     background: linear-gradient(90deg, transparent, rgba(96,165,250,0.8), rgba(56,189,248,0.8), transparent);
//     animation: shimmerLine 3s ease-in-out 1.2s infinite;
//   }
//   @keyframes shimmerLine {
//     0%   { left: -100%; }
//     40%, 100% { left: 100%; }
//   }

//   /* card corner accent */
//   .login-card::after {
//     content: '';
//     position: absolute;
//     top: -1px; right: -1px;
//     width: 80px; height: 80px;
//     background: radial-gradient(circle at top right, rgba(96,165,250,0.12), transparent 65%);
//     border-radius: 0 24px 0 0;
//   }

//   /* card header */
//   .card-logo {
//     width: 64px; height: 64px;
//     border-radius: 16px;
//     background: linear-gradient(135deg, #0a2558, #1e5ba8);
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     margin-bottom: 24px;
//     box-shadow: 0 8px 24px rgba(37,99,235,0.35);
//     animation: logoAppear 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.7s both;
//     position: relative;
//     overflow: hidden;
//     padding: 6px;
//   }
//   .card-logo::after {
//     content: '';
//     position: absolute;
//     inset: 0;
//     background: linear-gradient(135deg, rgba(255,255,255,0.15), transparent);
//     border-radius: 16px;
//     pointer-events: none;
//   }
//   .card-logo img {
//     width: 100%; height: 100%;
//     object-fit: contain;
//     position: relative;
//     z-index: 1;
//   }
//   .card-logo-text {
//     font-family: 'Playfair Display', serif;
//     font-size: 22px;
//     font-weight: 800;
//     color: white;
//     position: relative;
//     z-index: 1;
//   }

//   .card-title {
//     font-family: 'Playfair Display', serif;
//     font-size: 26px;
//     font-weight: 700;
//     color: white;
//     margin-bottom: 6px;
//     animation: fadeInUp 0.7s ease-out 0.8s both;
//   }

//   .card-subtitle {
//     font-size: 13px;
//     color: rgba(255,255,255,0.4);
//     margin-bottom: 36px;
//     animation: fadeInUp 0.7s ease-out 0.9s both;
//   }

//   /* ── form fields ── */
//   .field-group {
//     display: flex;
//     flex-direction: column;
//     gap: 20px;
//     margin-bottom: 28px;
//   }

//   .field {
//     display: flex;
//     flex-direction: column;
//     gap: 8px;
//     animation: fadeInUp 0.6s ease-out both;
//   }
//   .field:nth-child(1) { animation-delay: 1s; }
//   .field:nth-child(2) { animation-delay: 1.1s; }

//   .field-label {
//     font-size: 12px;
//     font-weight: 600;
//     color: rgba(255,255,255,0.5);
//     text-transform: uppercase;
//     letter-spacing: 0.8px;
//   }

//   .field-wrap {
//     position: relative;
//   }

//   .field-icon {
//     position: absolute;
//     left: 14px;
//     top: 50%;
//     transform: translateY(-50%);
//     color: rgba(255,255,255,0.25);
//     font-size: 15px;
//     pointer-events: none;
//     transition: color 0.3s ease;
//   }

//   .field-input {
//     width: 100%;
//     padding: 13px 14px 13px 42px;
//     background: rgba(255,255,255,0.06);
//     border: 1px solid rgba(255,255,255,0.1);
//     border-radius: 10px;
//     font-size: 14px;
//     font-family: 'DM Sans', sans-serif;
//     color: white;
//     outline: none;
//     transition: all 0.3s ease;
//     caret-color: #60a5fa;
//   }
//   .field-input::placeholder { color: rgba(255,255,255,0.2); }
//   .field-input:focus {
//     background: rgba(255,255,255,0.09);
//     border-color: rgba(96,165,250,0.6);
//     box-shadow: 0 0 0 3px rgba(96,165,250,0.1), 0 0 20px rgba(96,165,250,0.08);
//   }
//   .field-input:focus + .field-focus-bar { width: 100%; }
//   .field-input:disabled { opacity: 0.5; cursor: not-allowed; }

//   /* animated bottom bar on focus */
//   .field-focus-bar {
//     position: absolute;
//     bottom: 0; left: 50%;
//     transform: translateX(-50%);
//     height: 2px;
//     width: 0;
//     background: linear-gradient(90deg, #2563eb, #38bdf8);
//     border-radius: 0 0 10px 10px;
//     transition: width 0.4s cubic-bezier(0.16,1,0.3,1);
//     pointer-events: none;
//   }

//   .show-toggle {
//     position: absolute;
//     right: 12px;
//     top: 50%;
//     transform: translateY(-50%);
//     background: none;
//     border: none;
//     color: rgba(255,255,255,0.3);
//     cursor: pointer;
//     padding: 4px 6px;
//     font-size: 11px;
//     font-family: 'DM Sans', sans-serif;
//     font-weight: 600;
//     letter-spacing: 0.5px;
//     text-transform: uppercase;
//     transition: color 0.2s ease;
//   }
//   .show-toggle:hover { color: #60a5fa; }

//   /* ── error box ── */
//   .error-box {
//     display: flex;
//     align-items: flex-start;
//     gap: 10px;
//     background: rgba(239,68,68,0.1);
//     border: 1px solid rgba(239,68,68,0.3);
//     border-radius: 10px;
//     padding: 12px 14px;
//     margin-bottom: 20px;
//     animation: errorShake 0.4s cubic-bezier(0.36,0.07,0.19,0.97);
//   }
//   @keyframes errorShake {
//     10%, 90%  { transform: translateX(-2px); }
//     20%, 80%  { transform: translateX(4px); }
//     30%, 50%, 70% { transform: translateX(-4px); }
//     40%, 60%  { transform: translateX(4px); }
//   }
//   .error-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
//   .error-text { font-size: 13px; color: #fca5a5; line-height: 1.5; }

//   /* ── submit button ── */
//   .submit-btn {
//     width: 100%;
//     padding: 14px;
//     background: linear-gradient(135deg, #1d4ed8, #2563eb, #0ea5e9);
//     background-size: 200% auto;
//     color: white;
//     border: none;
//     border-radius: 10px;
//     font-size: 14px;
//     font-weight: 600;
//     font-family: 'DM Sans', sans-serif;
//     cursor: pointer;
//     letter-spacing: 0.3px;
//     transition: all 0.4s ease;
//     position: relative;
//     overflow: hidden;
//     animation: fadeInUp 0.7s ease-out 1.2s both;
//     box-shadow: 0 4px 20px rgba(37,99,235,0.3);
//   }
//   .submit-btn::before {
//     content: '';
//     position: absolute;
//     inset: 0;
//     background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%);
//     transform: translateX(-100%);
//     transition: transform 0.6s ease;
//   }
//   .submit-btn:hover:not(:disabled)::before { transform: translateX(100%); }
//   .submit-btn:hover:not(:disabled) {
//     background-position: right center;
//     box-shadow: 0 8px 32px rgba(37,99,235,0.45);
//     transform: translateY(-1px);
//   }
//   .submit-btn:active:not(:disabled) { transform: translateY(0); }
//   .submit-btn:disabled {
//     background: rgba(255,255,255,0.08);
//     color: rgba(255,255,255,0.3);
//     cursor: not-allowed;
//     box-shadow: none;
//   }

//   .btn-inner {
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     gap: 8px;
//     position: relative;
//     z-index: 1;
//   }

//   .spinner {
//     width: 16px; height: 16px;
//     border: 2px solid rgba(255,255,255,0.2);
//     border-top-color: white;
//     border-radius: 50%;
//     animation: spin 0.8s linear infinite;
//     flex-shrink: 0;
//   }
//   @keyframes spin {
//     to { transform: rotate(360deg); }
//   }

//   /* ── footer ── */
//   .card-footer {
//     text-align: center;
//     margin-top: 28px;
//     font-size: 12px;
//     color: rgba(255,255,255,0.18);
//     animation: fadeInUp 0.7s ease-out 1.4s both;
//     letter-spacing: 0.2px;
//   }

//   @keyframes fadeInUp {
//     from { opacity: 0; transform: translateY(14px); }
//     to   { opacity: 1; transform: translateY(0); }
//   }

//   /* responsive */
//   @media (max-width: 860px) {
//     .login-brand { display: none; }
//     .login-panel { width: 100%; }
//   }
// `;

// export default function Login() {
//     const navigate = useNavigate();

//     const [username, setUsername] = useState("");
//     const [password, setPassword] = useState("");
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState("");
//     const [showPassword, setShowPassword] = useState(false);
//     const [focusedField, setFocusedField] = useState(null);

//     const login = async (e) => {
//         e.preventDefault();
//         if (!username.trim() || !password.trim()) {
//             setError("Please enter both username and password");
//             return;
//         }
//         setLoading(true);
//         setError("");
//         try {
//             const response = await loginAPI(username, password);
//             if (response && response.data?.token) {
//                 localStorage.setItem("token", response.data.token);
//                 localStorage.setItem("username", username);
//                 navigate("/dashboard");
//             } else {
//                 setError(response?.message || "Login failed. Please try again.");
//             }
//         } catch (err) {
//             setError(err.message || "Something went wrong. Please try again.");
//             console.error("Login error:", err);
//         } finally {
//             setLoading(false);
//         }
//     };

//     return (
//         <>
//             <style>{STYLES}</style>
//             <div className="login-root">

//                 {/* Animated background */}
//                 <div className="login-bg" />
//                 <div className="login-grid" />
//                 <div className="orb orb-1" />
//                 <div className="orb orb-2" />
//                 <div className="orb orb-3" />
//                 <div className="orb orb-4" />

//                 {/* ── Left brand panel ── */}
//                 <div className="login-brand">
//                     <div className="brand-eyebrow">
//                         <span className="brand-eyebrow-dot" />
//                         Enterprise Portal
//                     </div>
//                     <h1 className="brand-title">
//                         Chairman's
//                         <span className="brand-title-accent">Desk</span>
//                     </h1>
//                     <p className="brand-desc">
//                         Real-time network inventory and operations overview.
//                         Monitor distributors, LCOs, and customer activity from a single command center.
//                     </p>
//                     <div className="brand-stats">
//                         <div className="brand-stat">
//                             <span className="brand-stat-value">24/7</span>
//                             <span className="brand-stat-label">Live Data</span>
//                         </div>
//                         <div className="brand-divider" />
//                         <div className="brand-stat">
//                             <span className="brand-stat-value">100%</span>
//                             <span className="brand-stat-label">Uptime</span>
//                         </div>
//                         <div className="brand-divider" />
//                         <div className="brand-stat">
//                             <span className="brand-stat-value">256-bit</span>
//                             <span className="brand-stat-label">Encryption</span>
//                         </div>
//                     </div>
//                 </div>

//                 {/* ── Right form panel ── */}
//                 <div className="login-panel">
//                     <div className="login-card">

//                         {/* Logo */}
//                         <div className="card-logo">
//                             <img
//                                 src="../dm_profile.png"
//                                 alt="Chairman Desk"
//                                 onError={(e) => {
//                                     e.target.style.display = "none";
//                                     e.target.nextSibling.style.display = "block";
//                                 }}
//                             />
//                             <span className="card-logo-text" style={{ display: "none" }}>C</span>
//                         </div>

//                         <h2 className="card-title">Welcome</h2>
//                         <p className="card-subtitle">Sign in to access your dashboard</p>

//                         {/* Error */}
//                         {error && (
//                             <div className="error-box" key={error}>
//                                 <span className="error-icon">⚠️</span>
//                                 <p className="error-text">{error}</p>
//                             </div>
//                         )}

//                         {/* Form */}
//                         <form onSubmit={login}>
//                             <div className="field-group">

//                                 {/* Username */}
//                                 <div className="field">
//                                     <label className="field-label">Username</label>
//                                     <div className="field-wrap">
//                                         <span className="field-icon" style={{ color: focusedField === "username" ? "#60a5fa" : undefined }}>
//                                             👤
//                                         </span>
//                                         <input
//                                             className="field-input"
//                                             type="text"
//                                             placeholder="Enter your username"
//                                             value={username}
//                                             onChange={(e) => setUsername(e.target.value)}
//                                             disabled={loading}
//                                             onFocus={() => setFocusedField("username")}
//                                             onBlur={() => setFocusedField(null)}
//                                             autoComplete="username"
//                                         />
//                                         <div className="field-focus-bar" />
//                                     </div>
//                                 </div>

//                                 {/* Password */}
//                                 <div className="field">
//                                     <label className="field-label">Password</label>
//                                     <div className="field-wrap">
//                                         <span className="field-icon" style={{ color: focusedField === "password" ? "#60a5fa" : undefined }}>
//                                             🔒
//                                         </span>
//                                         <input
//                                             className="field-input"
//                                             type={showPassword ? "text" : "password"}
//                                             placeholder="Enter your password"
//                                             value={password}
//                                             onChange={(e) => setPassword(e.target.value)}
//                                             disabled={loading}
//                                             onFocus={() => setFocusedField("password")}
//                                             onBlur={() => setFocusedField(null)}
//                                             autoComplete="current-password"
//                                             style={{ paddingRight: "60px" }}
//                                         />
//                                         <div className="field-focus-bar" />
//                                         <button
//                                             type="button"
//                                             className="show-toggle"
//                                             onClick={() => setShowPassword(!showPassword)}
//                                             disabled={loading}
//                                             tabIndex={-1}
//                                         >
//                                             {showPassword ? "Hide" : "Show"}
//                                         </button>
//                                     </div>
//                                 </div>

//                             </div>

//                             {/* Submit */}
//                             <button className="submit-btn" type="submit" disabled={loading}>
//                                 <span className="btn-inner">
//                                     {loading && <span className="spinner" />}
//                                     {loading ? "Signing in…" : "Sign In →"}
//                                 </span>
//                             </button>
//                         </form>

//                         <p className="card-footer">© 2026 Chairman Desk · All rights reserved</p>

//                     </div>
//                 </div>

//             </div>
//         </>
//     );
// }
